import React from 'react';
import { TFunction } from 'i18next';
import * as Yup from 'yup';
import { FieldChangeCallBackArgs, Values } from '@lib/interfaces/form';
import _get from 'lodash/get';
import Typography from '@lib/components/Typography/Typography';
import { Steps } from '@lib/enums/form';
import Select, {
  SelectComponents,
  SelectProps,
} from '@lib/components/Select/Select';
import {
  getOptionShape,
  getOptionValidation,
} from '@lib/utils/validationUtils';
import styles from '@lib/assets/styles/Form.module.scss';
import { GetLoadOptions } from '@lib/components/Select/types';
import UserOption from '@lib/components/Select/components/UserOption';
import AreaOption from '@lib/components/Select/components/AreaOption';
import AssetOption from '@lib/components/Select/components/AssetOption';
import UserSingleValue from '@lib/components/Select/components/UserSingleValue';
import AssetSingleValue from '@lib/components/Select/components/AssetSingleValue';
import ChipSingleValue from '@lib/components/Select/components/ChipSingleValue';
import SiteOption from '@lib/components/Select/components/SiteOption';
import { AssignationsType, FieldNames } from 'routes/Task/enums';
import RadioButtonAsButton from '@lib/components/RadioButtonAsButton/RadioButtonAsButton';
import RadioButtonGroup, {
  RadioButtonWrapViews,
} from '@lib/components/RadioButtonGroup/RadioButtonGroup';

type TaskFieldsArgs = {
  canQrCodeRelatedInfoUpdate?: boolean;
  getApproversLoadOptions: GetLoadOptions;
  getAssetsLoadOptions: GetLoadOptions;
  getSiteAreasLoadOptions: GetLoadOptions;
  getSitesLoadOptions: GetLoadOptions;
  getTemplateVersionsLoadOptions?: GetLoadOptions;
  getTemplatesLoadOptions?: GetLoadOptions;
  isCreateForm?: boolean;
  onCreateNewFormTemplate?: () => void;
  onViewFormTemplate?: (
    templateFormId: string,
    templateFormVersionId: string,
  ) => void;
  step?: Steps;
  t: TFunction<'translation', undefined>;
  isCreate: boolean;
};

const defaultSelectEntityBySiteIdProps = {
  isDisabledFn: (values: Values) =>
    _get(values, `${FieldNames.site}.value`) === undefined,
  getFieldKeyFn: (values: Values, name?: string) => {
    const selectedSiteId = _get(values, `${FieldNames.site}.value`);
    if (selectedSiteId) return `${name}-site-${selectedSiteId}`;
    return FieldNames.site;
  },
};

export default function getEditTaskScopeFields(args: TaskFieldsArgs) {
  const {
    canQrCodeRelatedInfoUpdate = true,
    getApproversLoadOptions,
    getAssetsLoadOptions,
    getSiteAreasLoadOptions,
    getSitesLoadOptions,
    step = Steps.step1,
    t,
  } = args;
  return [
    {
      name: FieldNames.assignationsTitle,
      customRender: () => (
        <Typography variant="h3" className={styles.blockStart}>
          {t('task-scope')}
        </Typography>
      ),
      step,
    },
    {
      name: FieldNames.assignationsType,
      element: RadioButtonGroup,
      validation: getOptionValidation({ required: true, t }),
      componentProps: {
        options: [
          {
            label: t('area-based'),
            value: AssignationsType.Area,
            leftIcon: 'location_on',
          },
          {
            label: t('equipment-based'),
            value: AssignationsType.Equipment,
            leftIcon: 'inventory',
          },
        ],
        RadioButtonComponent: RadioButtonAsButton,
        wrapView: RadioButtonWrapViews.row,
      },
      step,
    },
    {
      name: FieldNames.site,
      label: t('site'),
      element: Select,
      validation: getOptionValidation({ t, required: true }),
      componentProps: {
        isAsync: true,
        getLoadOptions: getSitesLoadOptions,
        defaultOptions: false,
        components: {
          Option: SiteOption,
          SingleValue: ChipSingleValue,
        },
        disabled: true,
        disabledTooltipProps: canQrCodeRelatedInfoUpdate
          ? undefined
          : {
              body: t('disabled-field-desc-for-task-site-area-asset'),
            },
      } as SelectProps,
      step,
    },
    {
      name: FieldNames.siteArea,
      label: t('area'),
      element: Select,
      validation: Yup.mixed().when(FieldNames.assignationsType, {
        is: (assignationType: { value?: string } | null) =>
          assignationType?.value === AssignationsType.Area,
        then: () =>
          Yup.object()
            .shape(getOptionShape(t, true))
            .required(t('required-field-error')),
        otherwise: () => Yup.mixed().nullable(),
      }),
      componentProps: {
        isAsync: true,
        getLoadOptions: getSiteAreasLoadOptions,
        defaultOptions: false,
        components: {
          Option: AreaOption,
          SingleValue: ChipSingleValue,
        },
        disabled: !canQrCodeRelatedInfoUpdate,
        disabledTooltipProps: {
          body: canQrCodeRelatedInfoUpdate
            ? t('choose-site-first')
            : t('disabled-field-desc-for-task-site-area-asset'),
        },
        menuPlacement: 'top',
      } as SelectProps,
      fieldChangeCallBack: ({
        type,
        setValue,
        name,
      }: FieldChangeCallBackArgs) => {
        if (type === 'change' && name === FieldNames.siteArea) {
          setValue(FieldNames.approver, null, {
            shouldValidate: false,
          });
          setValue(FieldNames.asset, null, {
            shouldValidate: false,
          });
        }
      },
      ...defaultSelectEntityBySiteIdProps,
      isOptionalFn: (values: Values) =>
        _get(values, [FieldNames.assignationsType, 'value']) !==
        AssignationsType.Area,
      isVisibleFn: (values: Values) =>
        _get(values, [FieldNames.assignationsType, 'value']) ===
        AssignationsType.Area,
      step,
    },
    {
      name: FieldNames.asset,
      label: t('asset'),
      element: Select,
      validation: Yup.mixed().when(FieldNames.assignationsType, {
        is: (assignationType: { value?: string } | null) =>
          assignationType?.value === AssignationsType.Equipment,
        then: () =>
          Yup.object()
            .shape(getOptionShape(t, true))
            .required(t('required-field-error')),
        otherwise: () => Yup.mixed().nullable(),
      }),
      componentProps: {
        isAsync: true,
        getLoadOptions: getAssetsLoadOptions,
        defaultOptions: false,
        components: {
          Option: AssetOption,
          SingleValue: AssetSingleValue,
        } as unknown as SelectComponents,
        disabledTooltipProps: {
          body: canQrCodeRelatedInfoUpdate
            ? t('choose-site-first')
            : t('disabled-field-desc-for-task-site-area-asset'),
        },
        menuPlacement: 'top',
      } as SelectProps,
      ...defaultSelectEntityBySiteIdProps,
      isOptionalFn: (values: Values) =>
        _get(values, [FieldNames.assignationsType, 'value']) !==
        AssignationsType.Equipment,
      isVisibleFn: (values: Values) =>
        _get(values, [FieldNames.assignationsType, 'value']) ===
        AssignationsType.Equipment,
      step,
      fieldChangeCallBack: ({
        type,
        setValue,
        name,
      }: FieldChangeCallBackArgs) => {
        if (type === 'change' && name === FieldNames.asset) {
          setValue(FieldNames.approver, null, {
            shouldValidate: false,
          });
          setValue(FieldNames.siteArea, null, {
            shouldValidate: false,
          });
        }
      },
    },
    {
      name: FieldNames.approver,
      label: t('approver'),
      element: Select,
      validation: getOptionValidation({ t, required: true }),
      componentProps: {
        isAsync: true,
        getLoadOptions: getApproversLoadOptions,
        defaultOptions: false,
        components: {
          Option: UserOption,
          SingleValue: UserSingleValue,
        } as unknown as SelectComponents,
        disabledTooltipProps: {
          body: t('choose-site-first'),
        },
        menuPlacement: 'top',
      } as SelectProps,
      ...defaultSelectEntityBySiteIdProps,
      step,
    },
  ];
}
